import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { AuthProvider } from './lib/AuthContext';
import './App.css';
import FrontPage from './components/FrontPage';
import Dashboard from './components/Dashboard';
import KPI from './components/KPI';
import AdminSettings from './components/AdminSettings';
import UserSettings from './components/UserSettings';
import FileUpload from './components/FileUpload';
import SideNav from './components/SideNav';
import ProtectedRoute from './components/ProtectedRoute';

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('Starting auth handling...');
        console.log('Full URL:', window.location.href);

        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          navigate('/?error=session');
          return;
        }

        if (!session) {
          // If no session, try to exchange the code
          const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href);
          
          if (error) {
            console.error('Session exchange error:', error);
            navigate('/?error=exchange');
            return;
          }

          if (!data.session) {
            console.error('No session returned from exchange');
            navigate('/?error=no-session');
            return;
          }

          console.log('Session exchange successful:', data.session.user.email);
        }

        // Navigate to dashboard after successful auth
        console.log('Navigating to dashboard...');
        navigate('/dashboard');
      } catch (error) {
        console.error('Error in auth callback:', error);
        if (error instanceof Error) {
          console.error('Error details:', error.message);
        }
        navigate('/?error=unknown');
      }
    };

    handleCallback();
  }, [location.search, navigate]);

  return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Autenticando...</p>
    </div>
  );
}

function App() {
  const [userRole, setUserRole] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUserRole();
  }, []);

  const fetchUserRole = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_data')
        .select('role')
        .eq('user_id', session.user.id)
        .single();

      if (error) throw error;
      if (data) {
        setUserRole(data.role);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to check if user has admin access
  const isAdmin = (role: number) => role === 4;

  // Function to check if user can upload files
  const canUploadFiles = (role: number) => role > 1;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthProvider>
      <Router>
        <div className="app">
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/" element={<FrontPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <div className="app-container">
                  <SideNav userRole={userRole} />
                  <main className="main-content">
                    <Dashboard />
                  </main>
                </div>
              </ProtectedRoute>
            } />
            <Route
              path="/kpi"
              element={
                <ProtectedRoute>
                  <div className="app-container">
                    <SideNav userRole={userRole} />
                    <main className="main-content">
                      <KPI />
                    </main>
                  </div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  {canUploadFiles(userRole) ? (
                    <div className="app-container">
                      <SideNav userRole={userRole} />
                      <main className="main-content">
                        <FileUpload />
                      </main>
                    </div>
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-settings"
              element={
                <ProtectedRoute>
                  {isAdmin(userRole) ? (
                    <div className="app-container">
                      <SideNav userRole={userRole} />
                      <main className="main-content">
                        <AdminSettings />
                      </main>
                    </div>
                  ) : (
                    <Navigate to="/user-settings" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-settings"
              element={
                <ProtectedRoute>
                  <div className="app-container">
                    <SideNav userRole={userRole} />
                    <main className="main-content">
                      <UserSettings />
                    </main>
                  </div>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
